import { useMemberGetQuery, API_BASE, getSubdomain } from "modules/members/api";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { useMemberAuthStore } from "modules/members/auth";


export interface EvidenceFile {
  id: string;
  filename: string;
  s3_key: string;
  uploaded_at: string;
  uploaded_by_id: string;
  file_size: number;
  content_type: string;
}

export interface EvidenceListData {
  evidence_files: EvidenceFile[];
  count: number;
}

export interface UploadUrlResponse {
  success: boolean;
  data: {
    upload_url: string;
    url: string;
    content_type: string;
  };
}

export interface SaveEvidenceRequest {
  filename: string;
  s3_key: string;
  file_size: number;
  content_type: string;
}

// Get upload URL for evidence
export const getEvidenceUploadUrl = async (
  reviewId: string,
  filename: string
): Promise<UploadUrlResponse> => {
  const accessToken = useMemberAuthStore.getState().accessToken;
  const response = await fetch(
    `${API_BASE}members/access-reviews/${reviewId}/evidence/upload-url`,
    {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        "authorization": "Bearer " + accessToken,
        "x-org-subdomain": getSubdomain(),
      },
      body: JSON.stringify({ filename }),
    }
  );

  if (!response.ok) {
    throw new Error(`Failed to get upload URL: ${response.statusText}`);
  }

  return response.json();
};

// Save evidence metadata after S3 upload
export const saveEvidenceMetadata = async (
  reviewId: string,
  data: SaveEvidenceRequest
): Promise<{ success: boolean; data: EvidenceListData }> => {
  const accessToken = useMemberAuthStore.getState().accessToken;
  const response = await fetch(
    `${API_BASE}members/access-reviews/${reviewId}/evidence`,
    {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        "authorization": "Bearer " + accessToken,
        "x-org-subdomain": getSubdomain(),
      },
      body: JSON.stringify(data),
    }
  );

  if (!response.ok) {
    throw new Error(`Failed to save evidence: ${response.statusText}`);
  }

  return response.json();
};

// Hook to list evidence files
export const useListEvidence = (reviewId: string) => {
  const { data, isLoading, refetch } = useMemberGetQuery<EvidenceListData>(
    ["mf", "evidence", reviewId],
    `/members/access-reviews/${reviewId}/evidence`,
    {
      enabled: !!reviewId,
    }
  );

  return {
    evidenceFiles: data?.evidence_files || [],
    count: data?.count || 0,
    isLoading,
    refetch,
  };
};

// Hook to get download URL
export const useGetEvidenceDownloadUrl = () => {
  return useMutation(
    async ({ reviewId, fileId }: { reviewId: string; fileId: string }) => {
      const accessToken = useMemberAuthStore.getState().accessToken;
      const response = await fetch(
        `${API_BASE}members/access-reviews/${reviewId}/evidence/${fileId}/download`,
        {
          method: "GET",
          headers: {
            "Content-Type": "application/json",
            "authorization": "Bearer " + accessToken,
            "x-org-subdomain": getSubdomain(),
          },
        }
      );

      if (!response.ok) {
        throw new Error(`Failed to get download URL: ${response.statusText}`);
      }

      return response.json();
    }
  );
};

// Hook to delete evidence
export const useDeleteEvidence = () => {
  const queryClient = useQueryClient();

  return useMutation(
    async ({ reviewId, fileId }: { reviewId: string; fileId: string }) => {
      const accessToken = useMemberAuthStore.getState().accessToken;
      const response = await fetch(
        `${API_BASE}members/access-reviews/${reviewId}/evidence/${fileId}`,
        {
          method: "DELETE",
          headers: {
            "Content-Type": "application/json",
            "authorization": "Bearer " + accessToken,
            "x-org-subdomain": getSubdomain(),
          },
        }
      );

      if (!response.ok) {
        throw new Error(`Failed to delete evidence: ${response.statusText}`);
      }

      return response.json();
    },
    {
      onSuccess: (_, variables) => {
        // Invalidate the evidence list query to refresh the data
        queryClient.invalidateQueries(["mf", "evidence", variables.reviewId]);
      },
    }
  );
};

// Evidence analysis types
export interface EvidenceDiscrepancy {
  type: "user_not_in_ploy" | "entitlement_mismatch" | "user_not_in_evidence";
  confidence: "high" | "medium" | "low";
  reasoning: string;
  suggested_action: "add_user_to_resource" | "update_entitlements" | "flag_for_removal";
  account_id?: string;
  account_identifier?: string;
  evidence_user?: {
    identifier: string;
    name?: string;
    role?: string;
    entitlements?: string[];
  };
  ploy_entitlements?: string[];
  evidence_entitlements?: string[];
  in_other_account_set?: boolean;
  source_evidence_file_id?: string;
}

export interface EvidenceAnalysis {
  status: string;
  analysed_at: string;
  analysed_by_id: string;
  model_version: string;
  evidence_file_ids: string[];
  analysis_timestamps: string[];
  summary: string;
  discrepancies: EvidenceDiscrepancy[];
}

// Hook to get existing analysis results — polls every 3s while status is "pending"
export const useGetEvidenceAnalysis = (accountSetId: string) => {
  const { data, isLoading, refetch } = useMemberGetQuery<EvidenceAnalysis | null>(
    ["mf", "evidence-analysis", accountSetId],
    `/members/access-reviews/account-sets/${accountSetId}/analysis`,
    {
      enabled: !!accountSetId,
      refetchInterval: (rawData: any) => {
        if (rawData?.data?.status === "pending") return 3000;
        return false;
      },
    }
  );

  return {
    analysis: data ?? null,
    isLoading,
    isPending: data?.status === "pending",
    refetch,
  };
};

// Hook to analyse evidence with Luna (returns 202 — analysis runs in background)
export const useAnalyseEvidence = () => {
  const queryClient = useQueryClient();

  return useMutation(
    async ({ accountSetId }: { accountSetId: string }) => {
      const accessToken = useMemberAuthStore.getState().accessToken;
      const response = await fetch(
        `${API_BASE}members/access-reviews/account-sets/${accountSetId}/analyse`,
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            "authorization": "Bearer " + accessToken,
            "x-org-subdomain": getSubdomain(),
          },
          body: JSON.stringify({ account_set_id: accountSetId }),
        }
      );

      if (!response.ok) {
        const errorBody = await response.json().catch(() => ({}));
        throw new Error(errorBody?.error || `Analysis failed: ${response.statusText}`);
      }

      return response.json();
    },
    {
      onSuccess: (_, variables) => {
        queryClient.invalidateQueries(["mf", "evidence-analysis", variables.accountSetId]);
      },
    }
  );
};

// Hook to upload evidence (combines all steps)
export const useUploadEvidence = () => {
  const queryClient = useQueryClient();

  return useMutation(
    async ({
      reviewId,
      file,
      onProgress,
    }: {
      reviewId: string;
      file: File;
      onProgress?: (progress: number) => void;
    }) => {
      // Step 1: Get upload URL
      const uploadUrlResponse = await getEvidenceUploadUrl(
        reviewId,
        file.name
      );

      if (!uploadUrlResponse.success) {
        throw new Error("Failed to get upload URL");
      }

      const { upload_url, url: s3Key, content_type } = uploadUrlResponse.data;

      // Step 2: Upload to S3
      await new Promise<void>((resolve, reject) => {
        const xhr = new XMLHttpRequest();

        if (onProgress) {
          xhr.upload.addEventListener("progress", (event) => {
            if (event.lengthComputable) {
              const percentComplete = (event.loaded / event.total) * 100;
              onProgress(percentComplete);
            }
          });
        }

        xhr.addEventListener("load", () => {
          if (xhr.status === 200 || xhr.status === 204) {
            resolve();
          } else {
            reject(new Error(`Upload failed with status ${xhr.status}`));
          }
        });

        xhr.addEventListener("error", () => {
          reject(new Error("Upload failed"));
        });

        xhr.open("PUT", upload_url);
        xhr.setRequestHeader("Content-Type", content_type);
        xhr.send(file);
      });

      // Step 3: Save metadata
      const saveResult = await saveEvidenceMetadata(reviewId, {
        filename: file.name,
        s3_key: s3Key,
        file_size: file.size,
        content_type,
      });

      return saveResult;
    },
    {
      onSuccess: (_, variables) => {
        // Invalidate the evidence list query to refresh the data
        queryClient.invalidateQueries(["mf", "evidence", variables.reviewId]);
      },
    }
  );
};